import React from 'react';
import type { GameMode } from '../hooks/useGameLogic';
import type { PurchasableModule } from '../hooks/usePurchases';

interface ModuleInfo {
  id: GameMode;
  name: string;
  emoji: string;
  description: string;
  gradient: string;
  isPaid: boolean;
  purchaseId?: PurchasableModule;
}

const modules: ModuleInfo[] = [
  {
    id: 'soft',
    name: 'Soft Mode',
    emoji: '🍺',
    description: 'Questions et actions fun pour bien commencer la soirée',
    gradient: 'from-blue-500 to-blue-700',
    isPaid: false,
  },
  {
    id: 'hard',
    name: 'Hard Mode',
    emoji: '💀',
    description: 'Pour ceux qui osent aller plus loin...',
    gradient: 'from-violet-600 to-purple-800',
    isPaid: true,
    purchaseId: 'hard',
  },
  {
    id: 'roulette',
    name: 'Débat Brûlant',
    emoji: '🔥',
    description: 'La roue tourne, le débat s\'enflamme !',
    gradient: 'from-green-500 to-green-800',
    isPaid: true,
    purchaseId: 'roulette',
  },
  {
    id: 'coquin',
    name: 'Coquin Mode',
    emoji: '🌶️',
    description: 'Réservé aux soirées entre adultes... 18+',
    gradient: 'from-red-500 to-red-800',
    isPaid: true,
    purchaseId: 'coquin',
  },
];

interface ModuleSelectorProps {
  onSelectModule: (mode: GameMode) => void;
  onLockedModule: (module: ModuleInfo) => void;
  isModulePurchased: (module: PurchasableModule) => boolean;
}

export default function ModuleSelector({ onSelectModule, onLockedModule, isModulePurchased }: ModuleSelectorProps) {
  const handleSelect = (module: ModuleInfo) => {
    if (module.isPaid && module.purchaseId && !isModulePurchased(module.purchaseId)) {
      onLockedModule(module);
      return;
    }
    onSelectModule(module.id);
  };

  return (
    <div className="w-full max-w-md mx-auto space-y-4">
      {modules.map((module, index) => {
        const isLocked = module.isPaid && module.purchaseId && !isModulePurchased(module.purchaseId);

        return (
          <button
            key={module.id}
            onClick={() => handleSelect(module)}
            className={`w-full relative overflow-hidden rounded-3xl p-5 text-left bg-gradient-to-r ${module.gradient} btn-press animate-slide-up shimmer`}
            style={{ animationDelay: `${index * 100}ms` }}
          >
            <div className="flex items-center gap-4">
              {/* Emoji */}
              <div className="text-4xl flex-shrink-0">{module.emoji}</div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h3 className="font-display font-bold text-lg text-white">
                    {module.name}
                  </h3>
                  {isLocked && (
                    <span className="text-sm">🔒</span>
                  )}
                </div>
                <p className="text-white/70 font-body text-sm mt-0.5 line-clamp-2">
                  {module.description}
                </p>
              </div>

              {/* Arrow */}
              <div className="text-white/40 text-xl flex-shrink-0">›</div>
            </div>

            {/* Lock overlay */}
            {isLocked && (
              <div className="absolute inset-0 bg-black/30 rounded-3xl" />
            )}
          </button>
        );
      })}
    </div>
  );
}

export { modules };
export type { ModuleInfo };
